<template>
  <NewBsBox
    :class="`tab-container tab-position-${position}`"
    :bs="{
      'cmp.tab': 'container',
      ...bs,
    }"
  >
    <slot
      name="headers"
      :cmp="TabHeaders"
      :attrs="headerAttrs"
    >
      <TabHeaders
        v-bind="headerAttrs"
      >
        <template
          v-if="$slots.actionBefore"
          #actionBefore
        >
          <slot name="actionBefore" />
        </template>
        <template
          v-if="$slots.action || $slots.actionAfter"
          #actionAfter
        >
          <slot name="action" />
          <slot name="actionAfter" />
        </template>
      </TabHeaders>

    </slot>
    <slot :currentTabItem="currentTabItem" />
  </NewBsBox>
</template>
<script lang="ts">
  import {
    defineComponent,
    PropType,
    ref,
    watch,
    computed,
    provide,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { findIndex } from 'lodash'
  import { BeerStyleObj } from '../../../core'
  import TabHeaders from './Headers.vue'
  import {
    TabItemModifier,
    TabHeaderItem,
    TabHeaderProps,
  } from './TabD'

  /**
   * 标签页切换组件
   */
  export default defineComponent({
    name: 'BeerTabs',
    components: {
      TabHeaders,
      NewBsBox,
    },
    props: {
      /**
       * @type {primary|success|danger|warning|secondary}
       * @ignore
       */
      modifier: {
        type: String as PropType<TabItemModifier>,
        default: 'primary',
      },
      /**
       * @ignore
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * @ignore
       */
      headerBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 可以直接透传 attrs
       *
       * @deprecate 4.1.0
       */
      headerProps: {
        type: Object as PropType<TabHeaderProps>,
        default: () => ({}),
      },
      /**
       * 当前激活的 tab name
       */
      modelValue: {
        type: [String, Number] as PropType<string>,
      },
      /**
       * 如果没有 modelValue，初始选中面板的 name
       */
      defaultModelValue: {
        type: [String, Number] as PropType<string>,
        required: false,
      },
      /**
       * tab列表
       */
      list: {
        type: Array as PropType<TabHeaderItem[]>,
        default: () => ([]),
      },
      /**
       * 标签的大小和空间
       * @type {s|m|l}
       */
      size: {
        type: String,
        default: 'm',
      },
      /**
       * 标签组的位置
       * @type {top|bottom|left|right}
       */
      position: {
        type: String,
        default: 'top',
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit, attrs }) {
      const tabList = ref(props.list)
      const localTabName = ref(props.modelValue || props.defaultModelValue || props.list?.[0]?.name)
      const currentTabItem = computed<TabHeaderItem>(() => {
        const tab = tabList.value.find(v => v.name === localTabName.value)
        return tab || { name: '-', label: '-', slots: {} }
      })
      watch(() => props.modelValue, val => {
        localTabName.value = val as string
      })
      watch(() => props.list, val => {
        tabList.value = val
      })
      provide('tabs', {
        localTabName,
        addTab(item: TabHeaderItem) {
          if (!localTabName.value && item.name) {
            localTabName.value = item.name
          }
          tabList.value = tabList.value.concat([item])
        },
        removeTab(name: string) {
          tabList.value = tabList.value.filter(i => i.name !== name)
        },
        updateTab(name: string, tabPaneProps: any) {
          const updateIndex = findIndex(tabList.value, { name })
          tabList.value[updateIndex] = tabPaneProps
        },
      })

      const headerAttrs = computed(() => ({
        // @ts-ignore
        list: tabList.value,
        modifier: props.modifier,
        size: props.size,
        bs: props.headerBs,
        ...props.headerProps,
        ...attrs,
        modelValue: localTabName.value,
        handleClickItem: (item: TabHeaderItem): void => {
          if (item.disabled) {
            return
          }
          localTabName.value = item.name
          emit('update:modelValue', item.name, item)
        },
      }))

      return {
        tabList,
        localTabName,
        currentTabItem,
        headerAttrs,
        TabHeaders,
      }
    },
  })
</script>
